import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import UseContext from '../../../../../../hooks/useContext';
import ApiDatabase from '../../../../../../server';
import Button from '../../../../../button';
import StyledInput from '../../../../../styledInput/StyledInput';
import { DateInput } from '../shared';
import { isEuNationality } from './nationalitySummary';

// Shared constant: a fresh default array would rebuild the options on every render.
const NO_NATIONALITIES = [];

const ERROR_KEYS = {
  INVALID_IDENTITY: 'errors.invalidIdentity',
  TEMP_WORKER_NOT_FOUND: 'errors.notFound',
};

// Built from the local date parts: toISOString would shift a midnight local
// date to the previous day for any timezone ahead of UTC.
export const toCalendarDateString = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());

/**
 * Lets an admin correct the candidate's names, birth date and nationality
 * while looking at the identity document.
 * @param {object} props
 * @param {string} props.tempWorkerId - temp_worker _id
 * @param {object} props.initialIdentity - { firstname, lastname, birthday: Date|null, idNationality }
 * @param {Array} props.nationalityList - documents of the getNationalities request
 * @param {function} props.onCorrected - called with the identity returned by the api
 */
const IdentityCorrection = ({ tempWorkerId, initialIdentity, nationalityList = NO_NATIONALITIES, onCorrected }) => {
  const { language } = UseContext();
  const text = (key, fallback = '') =>
    language?.getValue ? language.getValue(`identityCorrection.${key}`, fallback) : fallback;

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [birthday, setBirthday] = useState(null);
  const [idNationality, setIdNationality] = useState('');

  // Memoized: StyledInput resets its option list whenever it receives a new array.
  const nationalityOptions = useMemo(() => {
    const euLabel = language?.getValue ? language.getValue('identityCorrection.eu', 'UE') : 'UE';
    const nonEuLabel = language?.getValue
      ? language.getValue('identityCorrection.nonEu', 'Hors UE')
      : 'Hors UE';
    return nationalityList.map((nationality) => ({
      value: nationality._id,
      label: `${nationality.NationaliteM} · ${
        isEuNationality(nationality) ? euLabel : nonEuLabel
      }`,
    }));
  }, [nationalityList, language]);

  const open = () => {
    setFirstname(initialIdentity?.firstname ?? '');
    setLastname(initialIdentity?.lastname ?? '');
    setBirthday(isValidDate(initialIdentity?.birthday) ? initialIdentity.birthday : null);
    setIdNationality(initialIdentity?.idNationality ?? '');
    setIsOpen(true);
  };

  const hasEmptyField =
    !firstname.trim() || !lastname.trim() || !isValidDate(birthday) || !idNationality;
  const isSaveDisabled = isSaving || hasEmptyField || !tempWorkerId;

  const save = () => {
    if (isSaveDisabled) return;
    setIsSaving(true);
    ApiDatabase.postUpdateTempWorkerIdentityAdmin(
      {
        tempWorkerId,
        firstname,
        lastname,
        birthday: toCalendarDateString(birthday),
        idNationality,
      },
      (data) => {
        setIsSaving(false);
        if (data?.bestt === 'failed') {
          toast.warning(
            text('toasts.besttFailed', 'Identité corrigée dans Jobbiz. À corriger aussi dans BeSTT.')
          );
        } else {
          toast.success(text('toasts.corrected', 'Identité corrigée.'));
        }
        setIsOpen(false);
        onCorrected?.(data?.identity);
      },
      (err) => {
        setIsSaving(false);
        if (err?.name === 'CanceledError') return;
        const errorKey = ERROR_KEYS[err?.response?.data?.errorCode] || 'errors.generic';
        toast.error(text(errorKey, 'Une erreur est survenue.'));
      }
    );
  };

  if (!isOpen) {
    return (
      <div className="flex justify-end">
        <Button size="SM" color="SECONDARY" onClick={open} disabled={!tempWorkerId}>
          {text('open', 'Corriger l’identité')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3 bg-white border rounded-lg">
      <div className="ft-sm font-medium text-gray-900">{text('title', 'Corriger l’identité')}</div>
      <StyledInput
        type="text"
        label={text('firstname', 'Prénom')}
        value={firstname}
        maxLength={35}
        onChange={(e) => setFirstname(e.target.value)}
      />
      <StyledInput
        type="text"
        label={text('lastname', 'Nom')}
        value={lastname}
        maxLength={35}
        onChange={(e) => setLastname(e.target.value)}
      />
      <DateInput
        label={text('birthday', 'Date de naissance')}
        value={birthday}
        onChange={setBirthday}
      />
      <StyledInput
        type="select"
        label={text('nationality', 'Nationalité')}
        placeholder={text('nationalityPlaceholder', 'Choisir une nationalité')}
        list={nationalityOptions}
        value={idNationality}
        onChange={(e) => setIdNationality(e.target.value)}
      />
      <div className="flex gap-2 justify-end">
        <Button size="SM" color="SECONDARY" onClick={() => setIsOpen(false)} disabled={isSaving}>
          {text('cancel', 'Annuler')}
        </Button>
        <Button size="SM" color="PRIMARY" onClick={save} disabled={isSaveDisabled}>
          {isSaving ? text('saving', 'Enregistrement…') : text('save', 'Enregistrer')}
        </Button>
      </div>
    </div>
  );
};

export default IdentityCorrection;
